/**
 * GDPR (consent compliance) API hooks. Backed by /api/v1/gdpr in datahub-api.
 * Hand-shaped types mirror the Go models. Status is server-derived so the UI
 * can trust whatever comes back over the wire.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { client } from '../generated/client.gen';

export type ConsentStatus = 'granted' | 'expiring' | 'pending' | 'withdrawn';
export type ConsentType = 'newsletter' | 'marketing' | 'research' | 'all';

export interface ConsentAuditEntry {
  at: string;
  action: string;
  actor: string;
  meta?: Record<string, unknown>;
}

export interface Consent {
  id: string;
  contactId: string;
  type: ConsentType;
  scope: string;
  basis: 'consent' | 'legitimate' | 'contract' | 'withdrawn';
  status: ConsentStatus;
  source?: string;
  grantedAt?: string | null;
  expiresAt?: string | null;
  withdrawnAt?: string | null;
  remindersSent: number;
  lang?: string;
  ipAddress?: string;
  userAgent?: string;
  contactName?: string;
  contactEmail?: string;
  auditTrail?: ConsentAuditEntry[];
}

export interface Stats {
  active: number;
  expiring30: number;
  pending: number;
  withdrawn30: number;
}

export interface ListFilters {
  status?: string;
  type?: string;
  source?: string;
  q?: string;
  grantedFrom?: string;
  grantedTo?: string;
  page?: number;
  pageSize?: number;
}

const listKey = ['gdpr', 'consents', 'list'] as const;
const detailKey = (id: string) => ['gdpr', 'consents', 'detail', id] as const;
const statsKey = ['gdpr', 'stats'] as const;

function useListConsents(filters: ListFilters) {
  return useQuery({
    queryKey: [...listKey, filters] as const,
    queryFn: async () => {
      try {
        const { data } = await client.instance.get<{
          data: Consent[];
          pagination: { page: number; pageSize: number; total: number };
        }>('/gdpr/consents', { params: filters });
        return data;
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) {
          return { data: [], pagination: { page: 1, pageSize: 25, total: 0 } };
        }
        throw e;
      }
    },
  });
}

function useConsent(id: string) {
  return useQuery({
    queryKey: detailKey(id),
    queryFn: async (): Promise<Consent> => {
      const { data } = await client.instance.get<Consent>(`/gdpr/consents/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

function useStats() {
  return useQuery({
    queryKey: statsKey,
    queryFn: async (): Promise<Stats> => {
      try {
        const { data } = await client.instance.get<Stats>('/gdpr/stats');
        return data;
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) {
          return { active: 0, expiring30: 0, pending: 0, withdrawn30: 0 };
        }
        throw e;
      }
    },
    staleTime: 30 * 1000,
  });
}

export interface ResendBody {
  template: string;
  lang: string;
  scheduleAt?: string;
  oneClickUnsub: boolean;
}

function useResendOne() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ResendBody }) => {
      const { data } = await client.instance.post<{ jobId: string }>(
        `/gdpr/consents/${id}/resend`,
        body,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey }),
  });
}

function useBulkResend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ResendBody & { consentIds: string[] }) => {
      const { data } = await client.instance.post<{ jobId: string; queued: number }>(
        `/gdpr/consents/bulk-resend`,
        body,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey }),
  });
}

function useWithdraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await client.instance.post<Consent>(`/gdpr/consents/${id}/withdraw`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey });
      qc.invalidateQueries({ queryKey: statsKey });
    },
  });
}

function useBuildDSAR() {
  return useMutation({
    mutationFn: async (contactId: string) => {
      const { data } = await client.instance.post<{ jobId: string }>('/gdpr/dsar', { contactId });
      return data;
    },
  });
}

function useRTBF() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: string) => {
      const { data } = await client.instance.post<{ jobId: string }>('/gdpr/rtbf', {
        contactId,
        confirm: true,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey }),
  });
}

function useRunRenewalJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await client.instance.post<{ markedExpiring: number }>(
        '/gdpr/run-renewal-job',
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey });
      qc.invalidateQueries({ queryKey: statsKey });
    },
  });
}

function usePreviewRenewal() {
  return useMutation({
    mutationFn: async ({
      id,
      template,
      lang,
    }: {
      id: string;
      template: string;
      lang: string;
    }) => {
      const { data } = await client.instance.get<{ subject: string; html: string }>(
        `/gdpr/consents/${id}/preview-renewal`,
        { params: { template, lang } },
      );
      return data;
    },
  });
}

function buildLogExportURL() {
  const base = client.instance.defaults.baseURL ?? '';
  return `${base}/gdpr/consent-log.csv`;
}

export const gdprApi = {
  useListConsents,
  useConsent,
  useStats,
  useResendOne,
  useBulkResend,
  useWithdraw,
  useBuildDSAR,
  useRTBF,
  useRunRenewalJob,
  usePreviewRenewal,
  buildLogExportURL,
};
